import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, forwardRef, Input, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QuestionType, AnimationOption } from 'library-explorer';
import { AnswerDTO } from 'src/app/model/answerDTO.model';

@Component({
    selector: 'app-question-ordering',
    templateUrl: './question-ordering.component.html',
    styleUrls: ['./question-ordering.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuestionOrderingComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QuestionOrderingComponent implements ControlValueAccessor {
  @Input() animation: AnimationOption = AnimationOption.NONE;
  @Input() animationDelay = 0;

  @Input() public set answers(value: AnswerDTO[]) {
    if (!value) {
      return;
    }
  
  
    this.output = new Array(value.length).fill(null);
    this.containers = new Array(value.length).fill(null).map((_, i) => i);
    this.options = value?.map((item, i) => ({ ...item, index: i }));
  }

  public disabled = false;

  public options: Array<AnswerDTO> = [];
  public containers: number[];

  public activeOption: AnswerDTO = null;

  public output: any[] = [];

  onChange = (data: any) => {};
  onTouched = () => {};

  constructor() { }

  public writeValue(obj: any): void {}

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public setActiveOption(option: AnswerDTO = null): void {
    this.activeOption = option;
  }

  public dropActiveOptionToContainer(index: number): void {
    const containerValue = this.output?.[index];

    if (!this.activeOption?.hash) {
      this.activeOption = containerValue;
      return;
    }

    const prevOptionIndex = this.output.findIndex(opt => opt?.hash === this.activeOption.hash);
    this.output[index] = this.activeOption;
  
    this.handleOptionDropAndUpdatePrevContainer(prevOptionIndex, this.activeOption.hash, containerValue);
    this.valueChanged();
    this.activeOption = null;
  }

  public drop(event: CdkDragDrop<any>): void {
    this.activeOption = null;
    const fromContainerIndex = event.previousContainer.data?.index ?? -1;
    const toContainerIndex = event.container.data?.index ?? -1;
    const data = event.item.data;

    if (fromContainerIndex === toContainerIndex) {
      return;
    }

    const currentOutputValue = this.output?.[toContainerIndex];

    if (toContainerIndex !== -1) {     
      this.output[toContainerIndex] = data;
    } else {
      this.options.push(data);
    }

    this.handleOptionDropAndUpdatePrevContainer(fromContainerIndex, data?.hash, currentOutputValue);
    this.valueChanged();
  }

  private handleOptionDropAndUpdatePrevContainer(fromIndex: number, optionHash: string, prevOption: AnswerDTO): void {
    if (fromIndex !== -1) {
      this.output[fromIndex] = prevOption;
      return;
    }

    prevOption?.hash && this.options.push(prevOption);
    this.options = this.options?.filter(opt => opt.hash !== optionHash);
  }
  
  private valueChanged(): void {
    const completedCount = this.output.filter(opt => opt?.hash).length;

    if (completedCount !== this.containers.length) {
      this.onChange(null);
      return;
    }

    const result = this.output.map((answer, i) => ({
      questionHash: answer.hash,
      order: i
    }));
  
    this.onChange({
      type: QuestionType.ORDER_QUESTION,
      items: result
    });
  }

}
